import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Collapse,
} from '@mui/material';
import { Add, Description, Delete, ExpandMore, ExpandLess, Edit, Save } from '@mui/icons-material';
import { useTeacher } from '../../context/TeacherContext';
import apiAxiosInstance, { aiAxiosInstance } from '../../utils/axiosInstance';
import { Student } from '@/Types/Student';
import axios from 'axios';
import GoogleClassroomImport from './GoogleClassroomImport';
import VideoHelpButton from '../Common/VideoHelpButton';
import { ProcessedStudentWithDuplicates } from '../Groups/utils/duplicateDetection';
import { simpleDuplicateValidator } from '../Groups/utils/simpleDuplicateValidation';
import SimpleEditableStudentGrid from '../Groups/SimpleEditableStudentGrid';
import StudentList from '../Student/StudentList';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Using ProcessedStudentWithDuplicates from Groups/utils/duplicateDetection.ts

interface ErrorResponse {
  error: string;
  duplicates?: string[];
  details?: string;
}

interface ErrorMessageProps {
  error: string;
  duplicates?: string[];
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({ error, duplicates }) => (
  <Box>
    <Typography variant="body1" gutterBottom>
      {error}
    </Typography>
    {duplicates && (
      <>
        <Typography variant="body2" color="text.secondary">
          Duplicate students:
        </Typography>
        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
          {duplicates.map((name, index) => (
            <li key={index}>{name}</li>
          ))}
        </ul>
        <Typography variant="body2">
          Please remove duplicates before saving.
        </Typography>
      </>
    )}
  </Box>
);

interface BulkStudentData {
  firstName: string;
  lastName: string;
  studentId: string;
  gradeLevel: string;
  periodId?: string;
}

const AddStudents: React.FC = () => {
  const { teacher } = useTeacher();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [textInput, setTextInput] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [singleStudent, setSingleStudent] = useState<Partial<Student>>({
    firstName: '',
    lastName: '',
    middleName: '',
    gradeLevel: teacher?.gradeLevel || '',
    dateOfBirth: '',
    parentEmail: '',
    parentPhone: '',
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileType, setFileType] = useState<'text' | null>(null);
  const [processedStudents, setProcessedStudents] = useState<ProcessedStudentWithDuplicates[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [addFormExpanded, setAddFormExpanded] = useState(false);
  const [studentListKey, setStudentListKey] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Fetch existing students for duplicate checking
  useEffect(() => {
    const fetchStudents = async () => {
      if (!teacher?._id) return;
      
      try {
        const response = await apiAxiosInstance.get('/api/students');
        setAllStudents(response.data || []);
      } catch (error) {
        console.error('Error fetching students:', error);
        setAllStudents([]);
      }
    };
    
    fetchStudents();
  }, [teacher?._id]);

  // Check if user should be redirected to Google Classroom tab after auth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_classroom') === 'connected') {
      // Switch to Google Classroom tab (index 2)
      setTabValue(2);
      // Clear the URL parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const setErrorMessage = (message: string) => {
    setError({ error: message });
  };

  const handleSingleStudentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSingleStudent({
      ...singleStudent,
      [e.target.name]: e.target.value
    });
  };

  const handleSingleStudentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teacher?._id || !teacher?.gradeLevel) return;

    if (editMode && editingStudent) {
      // Edit mode - update existing student
      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const updatedStudent = {
          ...singleStudent,
          _id: editingStudent._id,
          teacherId: teacher._id,
          gradeLevel: singleStudent.gradeLevel || teacher.gradeLevel,
        };

        await apiAxiosInstance.put(`/api/students/${editingStudent._id}`, updatedStudent);
        setSuccess('Student updated successfully!');
        
        // Reset form and exit edit mode
        setEditMode(false);
        setEditingStudent(null);
        setSelectedStudentIds([]); // Clear selection
        setSingleStudent({
          firstName: '',
          lastName: '',
          middleName: '',
          gradeLevel: teacher.gradeLevel,
          dateOfBirth: '',
          parentEmail: '',
          parentPhone: '',
        });
        setAddFormExpanded(false);
        
        // Refresh the students list
        const studentsResponse = await apiAxiosInstance.get('/api/students');
        setAllStudents(studentsResponse.data || []);
        
        // Refresh StudentList component
        setStudentListKey(prev => prev + 1);
      } catch (err) {
        setErrorMessage('Failed to update student. Please try again.');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Add mode - check for duplicates before saving
    const studentToValidate: ProcessedStudentWithDuplicates = {
      firstName: singleStudent.firstName || '',
      lastName: singleStudent.lastName || '',
      middleName: singleStudent.middleName,
      gradeLevel: singleStudent.gradeLevel || teacher.gradeLevel || '',
      dateOfBirth: singleStudent.dateOfBirth,
      parentEmail: singleStudent.parentEmail,
      parentPhone: singleStudent.parentPhone,
    };
    
    const validation = simpleDuplicateValidator.validateStudent(studentToValidate, allStudents);
    
    if (!validation.isValid) {
      setError({ 
        error: validation.message,
        details: validation.suggestions.join('\n')
      });
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const studentData: Student = {
        ...singleStudent as Omit<Student, 'gradeLevel' | 'teacherId'>,
        teacherId: teacher._id,
        gradeLevel: teacher.gradeLevel,
      };

      const response = await apiAxiosInstance.post('/api/students', studentData);
      setSuccess('Student added successfully!');
      setSingleStudent({
        firstName: '',
        lastName: '',
        middleName: '',
        gradeLevel: teacher.gradeLevel,
        dateOfBirth: '',
        parentEmail: '',
        parentPhone: '',
      });
      
      // Refresh the students list
      const studentsResponse = await apiAxiosInstance.get('/api/students');
      setAllStudents(studentsResponse.data || []);
      
      // Refresh StudentList component
      setStudentListKey(prev => prev + 1);
    } catch (err) {
      setErrorMessage('Failed to add student. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    if (selectedStudentIds.length === 1) {
      const studentToEdit = allStudents.find(s => s._id === selectedStudentIds[0]);
      if (studentToEdit) {
        // Clear any previous errors/success messages
        setError(null);
        setSuccess(null);
        
        setEditingStudent(studentToEdit);
        setEditMode(true);
        setSingleStudent({
          firstName: studentToEdit.firstName || '',
          lastName: studentToEdit.lastName || '',
          middleName: studentToEdit.middleName || '',
          gradeLevel: studentToEdit.gradeLevel || teacher?.gradeLevel || '',
          dateOfBirth: studentToEdit.dateOfBirth || '',
          parentEmail: studentToEdit.parentEmail || '',
          parentPhone: studentToEdit.parentPhone || '',
        });
        setTabValue(0); // Switch to single student tab
        setAddFormExpanded(true); // Expand the form
      }
    }
  };

  const handleDeleteClick = () => {
    if (selectedStudentIds.length > 0) {
      setDeleteConfirmOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    const count = selectedStudentIds.length;
    try {
      await apiAxiosInstance.post('/api/students/bulk-delete', {
        studentIds: selectedStudentIds
      });
      setSelectedStudentIds([]);
      setDeleteConfirmOpen(false);
      setSuccess(`Successfully deleted ${count} student${count !== 1 ? 's' : ''}`);
      
      // Refresh the students list
      const studentsResponse = await apiAxiosInstance.get('/api/students');
      setAllStudents(studentsResponse.data || []);
      
      // Refresh StudentList component
      setStudentListKey(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting students:', error);
      setErrorMessage('Failed to delete students. Please try again.');
      setDeleteConfirmOpen(false);
    }
  };

  const handleAddClick = () => {
    // Clear any previous errors/success messages
    setError(null);
    setSuccess(null);
    
    // Reset to add mode
    setEditMode(false);
    setEditingStudent(null);
    setSingleStudent({
      firstName: '',
      lastName: '',
      middleName: '',
      gradeLevel: teacher?.gradeLevel || '',
      dateOfBirth: '',
      parentEmail: '',
      parentPhone: '',
    });
    setTabValue(0);
    setAddFormExpanded(true);
  };

  const handleTextSubmit = async () => {
    if (!textInput.trim()) {
      setErrorMessage('Please enter student information');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // First, send to AI for processing
      const aiResponse = await aiAxiosInstance.post<{ students: Student[] }>('/parse-students', {
        text: textInput,
        teacherGrade: teacher?.gradeLevel,
        teacherId: teacher?._id
      });

      const processedStudents = aiResponse.data.students;

      // Then, save the processed students
      const response = await apiAxiosInstance.post('/api/students/bulk-create', {
        students: processedStudents.map((student: Student) => ({
          ...student,
          teacherId: teacher?._id
        }))
      });

      // If there are any groups or periods to update
      const studentsWithGroups = processedStudents.filter((s: Student) => s.groupIds && s.groupIds.length > 0);
      const studentsWithPeriods = processedStudents.filter((s: Student) => s.periodId);

      // Update groups if needed
      if (studentsWithGroups.length > 0) {
        await Promise.all(studentsWithGroups.flatMap((student: Student) => 
          (student.groupIds || []).map(groupId =>
            apiAxiosInstance.post(`/api/groups/${groupId}/add-student`, {
              studentId: student._id
            })
          )
        ));
      }

      // Update periods if needed
      if (studentsWithPeriods.length > 0) {
        await Promise.all(studentsWithPeriods.map((student: Student) =>
          apiAxiosInstance.post(`/periods/${student.periodId}/add-student`, {
            studentId: student._id
          })
        ));
      }

      setSuccess(`Successfully added ${processedStudents.length} students!`);
      setTextInput('');

    } catch (err) {
      console.error('Error:', err);
      setErrorMessage('Failed to process student information. Please check the format and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] || null;
    if (!selected) return;

    if (processedStudents.length > 0) {
      setPendingFile(selected);
      setShowConfirmDialog(true);
    } else {
      processFile(selected);
    }
    if (event.target.value) event.target.value = '';
  };

  const processFile = (file: File) => {
    // Clear previous processed data when new file is uploaded
    setProcessedStudents([]);
    setSuccess(null);
    setError(null);

    const reader = new FileReader();

    if (
      file.type === 'text/plain' || 
      file.type === 'text/csv' || 
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.txt') || 
      file.name.endsWith('.csv')
    ) {
      setFileType('text');
      reader.onloadend = async () => {
        const result = reader.result as string;
        setTextInput(result);
        
        // Auto-process the file content (similar to groups page)
        if (result.trim()) {
          await processStudentsFromText(result);
        }
      };
      reader.readAsText(file);
    } else {
      setErrorMessage('Unsupported file type. Please upload a text or CSV file.');
    }
  };

  const handleFileSubmit = async () => {
    if (!file) {
      setErrorMessage('Please select a file');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('teacherGrade', teacher?.gradeLevel || '');

    try {
      const response = await apiAxiosInstance.post('/api/students/parse-file', formData);
      setSuccess(`Successfully added ${response.data.count} students!`);
      setFile(null);
    } catch (err) {
      setErrorMessage('Failed to process file. Please check the format and try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Extract processing logic to reusable function (same as groups page)
  const processStudentsFromText = async (text: string) => {
    if (!text.trim()) {
      setError({ error: 'Please enter student information' });
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const aiResponse = await aiAxiosInstance.post('/parse-students', {
        text: text,
        teacherGrade: teacher?.gradeLevel,
        teacherId: teacher?._id
      });

      const students = aiResponse.data.students || [];
      
      // Set processed students
      setProcessedStudents(students);
      
      // Validate students using simple validation
      const { valid, invalid } = simpleDuplicateValidator.validateStudentBatch(students, allStudents);
      
      if (invalid.length > 0) {
        setSuccess(`Students processed! ${invalid.length} student${invalid.length > 1 ? 's' : ''} need additional information to avoid confusion with existing students. Edit them in the table below.`);
      } else {
        setSuccess(`Students processed successfully! All ${valid.length} students are ready to save.`);
      }
    } catch (err: any) {
      console.error('Error processing students:', err);
      setError({ error: 'Failed to process students. Please check the format and try again.' });
    } finally {
      setLoading(false);
    }
  };

  const processData = async () => {
    if (!textInput.trim()) {
      setErrorMessage('Please provide text to process');
      return;
    }

    if (!teacher?._id) {
      setErrorMessage('Teacher ID is required');
      return;
    }

    setProcessedStudents([]);
    await processStudentsFromText(textInput);
  };

  const handleSaveStudents = async () => {
    if (processedStudents.length === 0) {
      setError({ error: 'No students to save' });
      return;
    }

    if (!teacher?._id) {
      setError({ error: 'Teacher ID is required' });
      return;
    }

    // Validate all students - save only the valid ones
    const { valid, invalid } = simpleDuplicateValidator.validateStudentBatch(processedStudents, allStudents);
    
    if (valid.length === 0) {
      setError({ 
        error: `No students ready to save. All ${invalid.length} student${invalid.length > 1 ? 's' : ''} need additional information to distinguish them from existing students.` 
      });
      return;
    }

    setLoading(true);
    try {
      // Use valid students directly (they're already the correct subset)
      const studentsToSave = valid.map(student => ({
        ...student,
        teacherId: teacher._id,
        gradeLevel: student.gradeLevel || teacher.gradeLevel,
        // Ensure all fields are properly included
        middleName: student.middleName?.trim() || undefined,
        dateOfBirth: student.dateOfBirth?.trim() || undefined,
        parentEmail: student.parentEmail?.trim() || undefined,
        parentPhone: student.parentPhone?.trim() || undefined,
      }));
      
      console.log('Saving students with data:', studentsToSave); // Debug log

      const response = await apiAxiosInstance.post('/api/students/bulk-create', {
        students: studentsToSave
      });

      const createdStudents = response.data.students || [];
      const actualSavedCount = createdStudents.length;
      
      // Refresh the students list FIRST before filtering
      const studentsResponse = await apiAxiosInstance.get('/api/students');
      const updatedAllStudents = studentsResponse.data || [];
      setAllStudents(updatedAllStudents);
      
      // Now filter out the saved students by checking if they exist in the updated student list
      // We need to check against the NEW student list to see which ones were saved
      const remainingStudents = processedStudents.filter(processedStudent => {
        // Check if this processed student now exists in the database
        const existsInDb = updatedAllStudents.some((dbStudent: Student) => 
          dbStudent.firstName.toLowerCase() === processedStudent.firstName.toLowerCase() &&
          dbStudent.lastName.toLowerCase() === processedStudent.lastName.toLowerCase() &&
          ((!dbStudent.middleName && !processedStudent.middleName) || 
           (dbStudent.middleName?.toLowerCase() === processedStudent.middleName?.toLowerCase())) &&
          dbStudent.gradeLevel === (processedStudent.gradeLevel || teacher.gradeLevel)
        );
        // Keep students that were NOT saved (don't exist in DB or were invalid)
        return !existsInDb;
      });
      
      setProcessedStudents(remainingStudents);
      
      // Provide detailed feedback about what was saved vs ignored
      let successMessage = `Successfully added ${actualSavedCount} student${actualSavedCount > 1 ? 's' : ''}!`;
      
      if (remainingStudents.length > 0) {
        successMessage += ` ${remainingStudents.length} student${remainingStudents.length > 1 ? 's' : ''} remaining - needs additional information.`;
        // Keep text input if there are remaining students to edit
      } else {
        // All students were saved, clear text input too
        setTextInput('');
      }
      
      setSuccess(successMessage);
      
      // Refresh StudentList component
      setStudentListKey(prev => prev + 1);
      
    } catch (err) {
      console.error('Error:', err);
      if (axios.isAxiosError(err) && err.response?.data) {
        const errorData = err.response.data as ErrorResponse;
        if (errorData.duplicates) {
          setError({
            error: 'Some students could not be added due to duplicate IDs',
            details: 'Please check the student list and try again.',
            duplicates: errorData.duplicates
          });
        } else {
          setError(errorData);
        }
      } else {
        setError({ error: 'Failed to save students. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStudent = (index: number) => {
    setProcessedStudents(prev => prev.filter((_, i) => i !== index));
  };

  // removed image paste/upload support

  const handleConfirmDiscard = () => {
    if (pendingFile) {
      processFile(pendingFile);
    }
    setShowConfirmDialog(false);
    setPendingFile(null);
  };

  const handleCancelDiscard = () => {
    setShowConfirmDialog(false);
    setPendingFile(null);
  };

  const resetForm = () => {
    setSingleStudent({
      firstName: '',
      lastName: '',
      middleName: '',
      gradeLevel: teacher?.gradeLevel || '',
      dateOfBirth: '',
      parentEmail: '',
      parentPhone: '',
    });
  };

  const handleBulkImport = (data: BulkStudentData[]) => {
    // Process the data without intervention fields
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {/* Top Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddClick}
          startIcon={<Add />}
        >
          Add Student(s)
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleEditClick}
          disabled={selectedStudentIds.length !== 1}
          startIcon={<Edit />}
        >
          Edit
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteClick}
          disabled={selectedStudentIds.length === 0}
          startIcon={<Delete />}
        >
          Delete {selectedStudentIds.length > 0 && `(${selectedStudentIds.length})`}
        </Button>
      </Box>

      {/* Global Success/Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error.error}
          {error.details && (
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
              {error.details}
            </Typography>
          )}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Collapsible Add/Edit Students Form */}
      <Collapse in={addFormExpanded}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
              {editMode ? 'Edit Student' : 'Add Students'}
            </Typography>
            {!editMode && (
              <VideoHelpButton 
                url="https://www.youtube.com/watch?v=-010_Cxb7Bg"
                title="How to Add Students"
                buttonLabel="Watch tutorial on adding students"
              />
            )}
          </Box>

        {!editMode && (
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label="Single Student" />
            <Tab label="Multiple Students" />
            <Tab label="Google Classroom" />
          </Tabs>
        )}
        {editMode && (
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Editing Student: {editingStudent?.firstName} {editingStudent?.lastName}
          </Typography>
        )}

        <TabPanel value={tabValue} index={0}>
          <form onSubmit={handleSingleStudentSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={singleStudent.firstName}
                  onChange={handleSingleStudentChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={singleStudent.lastName}
                  onChange={handleSingleStudentChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Middle Name (Optional)"
                  name="middleName"
                  value={singleStudent.middleName || ''}
                  onChange={handleSingleStudentChange}
                  helperText="Helps distinguish students with similar names"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Grade Level"
                  name="gradeLevel"
                  value={singleStudent.gradeLevel}
                  onChange={handleSingleStudentChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth (Optional)"
                  name="dateOfBirth"
                  type="date"
                  value={singleStudent.dateOfBirth || ''}
                  onChange={handleSingleStudentChange}
                  InputLabelProps={{ shrink: true }}
                  helperText="Helps prevent duplicate students"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Parent Email (Optional)"
                  name="parentEmail"
                  type="email"
                  value={singleStudent.parentEmail || ''}
                  onChange={handleSingleStudentChange}
                  helperText="For communication and duplicate prevention"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Parent Phone (Optional)"
                  name="parentPhone"
                  type="tel"
                  value={singleStudent.parentPhone || ''}
                  onChange={handleSingleStudentChange}
                  helperText="For communication and duplicate prevention"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : (editMode ? <Save /> : <Add />)}
                  >
                    {editMode ? 'Save Changes' : 'Add Student'}
                  </Button>
                  {editMode && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditMode(false);
                        setEditingStudent(null);
                        setSingleStudent({
                          firstName: '',
                          lastName: '',
                          middleName: '',
                          gradeLevel: teacher?.gradeLevel || '',
                          dateOfBirth: '',
                          parentEmail: '',
                          parentPhone: '',
                        });
                        setAddFormExpanded(false);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        </TabPanel>

        {!editMode && (
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Add Multiple Students
              </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enter text or upload a file (supports .txt and .csv).
              </Typography>
              
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  // image paste support removed
                  placeholder={`Paste or type student information here...

Supported formats:
- Free text describing students
- CSV format: FirstName,LastName,GradeLevel
- One student per line`}
                  disabled={loading}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                      fontFamily: 'monospace',
                    }
                  }}
                />
                
              </Box>

              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                mb: 2 
              }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="text/plain,text/csv,.csv,application/vnd.ms-excel"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />

                <Button
                  variant="contained"
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={<Description />}
                  disabled={loading}
                >
                  Upload File
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={processData}
                  disabled={loading || !textInput.trim()}
                >
                  Process Data
                </Button>
              </Box>

              {!processedStudents.length && (
                <>
                  {fileType === 'text' && textInput && (
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        p: 1,
                        mb: 2,
                        backgroundColor: 'background.paper',
                        position: 'relative',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        File Preview
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: '200px',
                          overflow: 'auto',
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          p: 1,
                          bgcolor: 'grey.100',
                          borderRadius: 1,
                        }}
                      >
                        {textInput}
                      </Box>
                      {loading && (
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          zIndex: 1,
                        }}>
                          <CircularProgress />
                        </Box>
                      )}
                    </Paper>
                  )}
                </>
              )}

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 2,
                    '& .MuiAlert-message': {
                      width: '100%'
                    }
                  }}
                >
                  {error.duplicates ? (
                    <ErrorMessage error={error.error} duplicates={error.duplicates} />
                  ) : (
                    error.error
                  )}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}
            </Box>
          </Box>

          {processedStudents.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <SimpleEditableStudentGrid
                students={processedStudents}
                existingStudents={allStudents}
                onStudentsChange={setProcessedStudents}
                onRemoveStudent={handleRemoveStudent}
              />
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Students with duplicate information will be ignored
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSaveStudents}
                  disabled={loading || simpleDuplicateValidator.validateStudentBatch(processedStudents, allStudents).valid.length === 0}
                >
                  {(() => {
                    const { valid } = simpleDuplicateValidator.validateStudentBatch(processedStudents, allStudents);
                    return valid.length > 0 
                      ? `Save Ready Students (${valid.length})`
                      : 'No Students Ready';
                  })()}
                </Button>
              </Box>
            </Box>
          )}
          </TabPanel>
        )}

        {!editMode && (
          <TabPanel value={tabValue} index={2}>
            <GoogleClassroomImport onStudentsAdded={() => setStudentListKey(prev => prev + 1)} />
          </TabPanel>
        )}
      </Paper>
      </Collapse>

      {/* Student List Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
          Student List
        </Typography>
        <StudentList 
          key={studentListKey} 
          selectedIds={selectedStudentIds}
          onSelectionChange={setSelectedStudentIds}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
        />
      </Box>

      {/* Add confirmation dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelDiscard}
        aria-labelledby="discard-dialog-title"
        aria-describedby="discard-dialog-description"
      >
        <DialogTitle id="discard-dialog-title">
          Unsaved Processed Data
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="discard-dialog-description">
            You have processed students that haven't been saved yet. 
            Do you want to discard them and process the new file?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDiscard} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDiscard} 
            color="error"
            variant="contained"
          >
            Discard and Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedStudentIds.length} student{selectedStudentIds.length !== 1 ? 's' : ''}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddStudents; 